// Dados PUROS de moedas — sem dependência de browser/Firebase.
//
// Vive separado de `core/userSettings.js` (que importa o SDK Firebase e só
// roda no browser) pra que o BACKEND (Pages Functions / Worker de IA) também
// possa importar a lista de moedas suportadas sem puxar o Firebase junto.
//
// `userSettings.js` re-exporta `SUPPORTED_CURRENCIES` e `findCurrency` daqui
// pra manter compat com todos os imports existentes no frontend.

// Lista de moedas suportadas. `defaultRate` = quanto vale 1 unidade da moeda
// em BRL — um valor APROXIMADO de partida (cotações de mai/2026), só pra o
// usuário não começar do zero quando a API estiver indisponível.
export const SUPPORTED_CURRENCIES = [
  { code: 'BRL', name: 'Real',              symbol: 'R$',   country: 'Brasil',         defaultRate: 1 },
  { code: 'USD', name: 'Dólar',             symbol: 'US$',  country: 'EUA',            defaultRate: 5.00 },
  { code: 'EUR', name: 'Euro',              symbol: '€',    country: 'União Europeia', defaultRate: 5.80 },
  { code: 'GBP', name: 'Libra',             symbol: '£',    country: 'Reino Unido',    defaultRate: 6.70 },
  { code: 'CHF', name: 'Franco Suíço',      symbol: 'CHF',  country: 'Suíça',          defaultRate: 6.10 },
  { code: 'ARS', name: 'Peso Argentino',    symbol: 'AR$',  country: 'Argentina',      defaultRate: 0.0035 },
  { code: 'CAD', name: 'Dólar Canadense',   symbol: 'C$',   country: 'Canadá',         defaultRate: 3.65 },
  { code: 'AUD', name: 'Dólar Australiano', symbol: 'A$',   country: 'Austrália',      defaultRate: 3.25 },
  { code: 'JPY', name: 'Iene',              symbol: '¥',    country: 'Japão',          defaultRate: 0.033 },
  { code: 'CNY', name: 'Yuan',              symbol: 'CN¥',  country: 'China',          defaultRate: 0.69 },
  { code: 'INR', name: 'Rupia',             symbol: '₹',    country: 'Índia',          defaultRate: 0.058 },
  { code: 'KRW', name: 'Won',               symbol: '₩',    country: 'Coreia do Sul',  defaultRate: 0.0036 },
  { code: 'RUB', name: 'Rublo',             symbol: '₽',    country: 'Rússia',         defaultRate: 0.056 },
  { code: 'ZAR', name: 'Rand',              symbol: 'R',    country: 'África do Sul',  defaultRate: 0.28 },
  { code: 'MXN', name: 'Peso Mexicano',     symbol: 'MX$',  country: 'México',         defaultRate: 0.27 },
  { code: 'CLP', name: 'Peso Chileno',      symbol: 'CLP$', country: 'Chile',          defaultRate: 0.0054 },
  { code: 'COP', name: 'Peso Colombiano',   symbol: 'COL$', country: 'Colômbia',       defaultRate: 0.0013 },
  { code: 'PEN', name: 'Sol Peruano',       symbol: 'S/',   country: 'Peru',           defaultRate: 1.39 },
  { code: 'UYU', name: 'Peso Uruguaio',     symbol: '$U',   country: 'Uruguai',        defaultRate: 0.13 },
];

export const SUPPORTED_CURRENCY_CODES = SUPPORTED_CURRENCIES.map((c) => c.code);

export function findCurrency(code) {
  return SUPPORTED_CURRENCIES.find((c) => c.code === code) || null;
}

// Markup padrão da VET (Valor Efetivo Total) sobre a cotação COMERCIAL da API,
// em pontos percentuais. Cobre o spread do banco/corretora + IOF que o
// viajante paga de verdade na ponta (compra de moeda / gasto no cartão).
//
// É só o FALLBACK: o valor real é configurável pelo admin em
// `appConfig/currency.vetMarkupPct` (painel /admin.html) e o usuário pode
// sobrescrever a taxa final manualmente no modal de cotações.
export const DEFAULT_VET_MARKUP_PCT = 6;

/**
 * Arredonda uma cotação pra 2 casas decimais.
 *
 * Salvaguarda: moedas de valor muito baixo (ARS ~0,0035, COP ~0,0013, KRW,
 * CLP…) virariam 0,00 em 2 casas — e taxa 0 é tratada como "sem cotação",
 * quebrando a conversão. Nesses casos (o 2-casas daria 0) preservamos ~4
 * dígitos significativos pra não perder a cotação.
 */
export function roundRate(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return v;
  const two = Math.round(v * 100) / 100;
  if (two > 0) return two;
  const decimals = Math.min(10, 2 - Math.floor(Math.log10(v)));
  const f = Math.pow(10, decimals);
  return Math.round(v * f) / f;
}
